import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { AccessDeniedService } from '@core/services/access-denied.service';
import { AccessDeniedComponent } from './access-denied.component';

/**
 * The one global access-denied surface — mounted once at the app root. When an opted-in read comes back
 * 403, `forbiddenInterceptor` calls `AccessDeniedService.show(message)` and this overlay renders the
 * shared {@link AccessDeniedComponent} over the whole page carrying the backend's reason. Navigating away
 * (the "Go to dashboard" link, or any other route change) auto-dismisses it via the service.
 *
 * This replaces per-component 403 wiring for the authoritative single-read screens; the PMS read pages
 * keep their own "all scoped probes exhausted" panel because their 403s are benign fall-throughs.
 */
@Component({
  selector: 'app-access-denied-overlay',
  standalone: true,
  imports: [AccessDeniedComponent],
  template: `
    @if (svc.state(); as s) {
      <div class="ad-overlay">
        <app-access-denied
          [fill]="true"
          [variant]="s.variant"
          [message]="s.message"
          [homeLink]="s.homeLink ?? ['/dashboard']"
          [homeLabel]="s.homeLabel ?? 'Go to dashboard'"
        />
      </div>
    }
  `,
  styles: [
    `
      .ad-overlay {
        position: fixed;
        inset: 0;
        z-index: 1200;
        display: grid;
        place-items: center;
        overflow: auto;
        padding: 24px;
        background: var(--bg-app, #f6f6fb);
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AccessDeniedOverlayComponent {
  readonly svc = inject(AccessDeniedService);
}
